import AppConstants from 'general/constants/AppConstants';
import AppRoute from 'general/constants/AppRoute';

const ADMIN_UI_ROLES = [AppConstants.userRole.admin, AppConstants.userRole.operator];
const CUSTOMER_UI_MODE = 'CUSTOMER';
const CMS_UI_MODE = 'CMS';
const AGENCY_CMS_ROLES = [
  AppConstants.userAgencyType.sale,
  AppConstants.userAgencyType.investor,
  AppConstants.userAgencyType.landlord,
  AppConstants.userAgencyType.group,
];
const AGENCY_CMS_ROLE_PRIORITY = [
  AppConstants.userAgencyType.landlord,
  AppConstants.userAgencyType.investor,
  AppConstants.userAgencyType.group,
  AppConstants.userAgencyType.sale,
];

const normalizeRole = (role) => String(role || '').trim().toUpperCase();

const pushRole = (roles, role) => {
  const normalizedRole = normalizeRole(role);
  if (normalizedRole && !roles.includes(normalizedRole)) {
    roles.push(normalizedRole);
  }
};

const pushRoleList = (roles, roleList) => {
  if (Array.isArray(roleList)) {
    roleList.forEach((role) => pushRole(roles, role?.role || role));
    return;
  }

  if (typeof roleList === 'string') {
    roleList
      .split(',')
      .map((role) => role.trim())
      .filter(Boolean)
      .forEach((role) => pushRole(roles, role));
  }
};

const getAgencyRoles = (user, agencyRoleList = []) => {
  const roles = [];
  pushRole(roles, user?.agency?.primaryRole);
  pushRole(roles, user?.agency?.role);
  pushRole(roles, user?.primaryRole);
  pushRoleList(roles, user?.agency?.requestedCommissionModes);
  pushRoleList(roles, user?.requestedCommissionModes);

  if (Array.isArray(user?.agency?.commissions)) {
    user.agency.commissions.forEach((commission) => pushRole(roles, commission?.role));
  }

  pushRoleList(roles, agencyRoleList);

  return roles;
};

const getPrimaryAgencyRole = (user, agencyRoleList = []) => {
  const directAgencyRole = normalizeRole(user?.agency?.primaryRole || user?.agency?.role || user?.primaryRole);
  if (AGENCY_CMS_ROLES.includes(directAgencyRole)) return directAgencyRole;

  const agencyRoles = getAgencyRoles(user, agencyRoleList).filter((role) => AGENCY_CMS_ROLES.includes(role));
  if (agencyRoles.length === 1) return agencyRoles[0];

  return AGENCY_CMS_ROLE_PRIORITY.find((role) => agencyRoles.includes(role)) || '';
};

const RoleUtils = {
  uiModes: {
    customer: CUSTOMER_UI_MODE,
    cms: CMS_UI_MODE,
  },

  isAdminUiRole(role) {
    return ADMIN_UI_ROLES.includes(normalizeRole(role));
  },

  isCustomerUiMode(uiMode) {
    return !uiMode || uiMode === CUSTOMER_UI_MODE;
  },

  isCustomerUiRole(role, uiMode = CUSTOMER_UI_MODE) {
    if (!this.isCustomerUiMode(uiMode)) return false;
    return !!role && !this.isAdminUiRole(role);
  },

  hasCmsUi(user, agencyRoleList = []) {
    if (!user?.role) return false;
    if (this.isAdminUiRole(user.role)) return true;
    return AGENCY_CMS_ROLES.includes(getPrimaryAgencyRole(user, agencyRoleList));
  },

  getCmsHomeRoute(user, agencyRoleList = []) {
    if (!user?.role) return AppRoute.dashboard;
    if (this.isAdminUiRole(user.role)) return AppRoute.dashboard;

    const agencyRole = getPrimaryAgencyRole(user, agencyRoleList);
    if (!AGENCY_CMS_ROLES.includes(agencyRole)) return AppRoute.customerHome;
    if (agencyRole === AppConstants.userAgencyType.investor) return AppRoute.investorDashboard;
    if (agencyRole === AppConstants.userAgencyType.landlord) return AppRoute.landlordDashboard;
    if (agencyRole === AppConstants.userAgencyType.group) return AppRoute.groupDashboard;
    return AppRoute.saleDashboard;
  },

  getPrimaryAgencyRole,
  getAgencyRoles,
};

export default RoleUtils;
